import { useState, useEffect } from "react";
import { copyText } from "@/lib/clipboard";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Plus, 
  Copy, 
  Check, 
  ExternalLink, 
  BarChart3, 
  MoreVertical, 
  Pencil, 
  Trash2,
  Search,
  Link2,
  Calendar as CalendarIcon,
  Upload,
  QrCode,
  Download,
  Lock
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { id } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Route React yang tidak boleh dipakai sebagai alias
const RESERVED_ROUTES = [
  "register",
  "api",
  "login",
  "dashboard",
  "links",
  "settings",
  "analytics",
  "microsites",
  "subscription",
  "qrcode",
  "admin"
];

const LinksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  // Form states
  const [formUrl, setFormUrl] = useState("");
  const [formAlias, setFormAlias] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formExpiry, setFormExpiry] = useState(null);
  const [formActive, setFormActive] = useState(true);
  const [formShortCode, setFormShortCode] = useState("");
  const [bulkUrls, setBulkUrls] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Alias validation state
    const [aliasStatus, setAliasStatus] = useState(null);
  // null | checking | available | taken | invalid

    const [isCheckingAlias, setIsCheckingAlias] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);
  
  // realtime alias checker
useEffect(() => {
  const delayDebounce = setTimeout(() => {
    if (formAlias) {
      checkAliasAvailability(formAlias);
    } else {
      setAliasStatus(null);
    }
  }, 500);

  return () => clearTimeout(delayDebounce);
}, [formAlias]);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/links`, { withCredentials: true });
      setLinks(response.data);
    } catch (error) {
      toast.error("Gagal memuat links");
    } finally {
      setIsLoading(false);
    }
  };
  
  // alias custom check
  const checkAliasAvailability = async (alias) => {
      if (alias.length < 3) {
          setAliasStatus(null);
          return;
            }
  if (!alias) {
    setAliasStatus(null);
    return;
  }

  // validasi karakter
  const valid = /^[a-zA-Z0-9-]+$/.test(alias);
  if (!valid) {
    setAliasStatus("invalid");
    return;
  }

  // cek reserved route React
  if (RESERVED_ROUTES.includes(alias.toLowerCase())) {
    setAliasStatus("invalid");
    return;
  }

  setIsCheckingAlias(true);
  setAliasStatus("checking");

  try {
    const res = await axios.get(`${API}/links/check-alias/${alias}`);
    setAliasStatus(res.data.available ? "available" : "taken");
  } catch (err) {
    setAliasStatus("taken");
  } finally {
    setIsCheckingAlias(false);
  }
};

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formUrl) {
      toast.error("URL harus diisi");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/links`,
        {
          original_url: formUrl,
          custom_alias: formAlias || null,
          title: formTitle || null,
          expires_at: formExpiry ? formExpiry.toISOString() : null,
          is_active: formActive
        },
        { withCredentials: true }
      );
      setLinks([response.data, ...links]);
      resetForm();
      setCreateDialogOpen(false);
      toast.success("Link berhasil dibuat dengan QR Code!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal membuat link");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedLink) return;

    setIsSubmitting(true);
    try {
      const response = await axios.patch(
        `${API}/links/${selectedLink.link_id}`,
        {
          title: formTitle || undefined,
          short_code: formShortCode !== selectedLink.short_code ? formShortCode : undefined,
          expires_at: formExpiry ? formExpiry.toISOString() : undefined,
          is_active: formActive
        },
        { withCredentials: true }
      );
      setLinks(links.map(l => l.link_id === selectedLink.link_id ? response.data : l));
      resetForm();
      setEditDialogOpen(false);
      toast.success("Link berhasil diupdate!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal mengupdate link");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (linkId) => {
    if (!confirm("Yakin ingin menghapus link ini?")) return;

    try {
      await axios.delete(`${API}/links/${linkId}`, { withCredentials: true });
      setLinks(links.filter(l => l.link_id !== linkId));
      toast.success("Link berhasil dihapus!");
    } catch (error) {
      toast.error("Gagal menghapus link");
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    const urls = bulkUrls.split("\n").filter(u => u.trim());
    if (urls.length === 0) {
      toast.error("Masukkan minimal 1 URL");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/links/bulk`,
        { urls },
        { withCredentials: true }
      );
      setLinks([...response.data, ...links]);
      setBulkUrls("");
      setBulkDialogOpen(false);
      toast.success(`${response.data.length} link berhasil dibuat!`);
    } catch (error) {
      toast.error("Gagal membuat bulk links");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (link) => {
    setSelectedLink(link);
    setFormUrl(link.original_url);
    setFormTitle(link.title || "");
    setFormShortCode(link.short_code);
    setFormExpiry(link.expires_at ? new Date(link.expires_at) : null);
    setFormActive(link.is_active);
    setEditDialogOpen(true);
  };

  const openQrDialog = (link) => {
    setSelectedLink(link);
    setQrDialogOpen(true);
  };

  const resetForm = () => {
    setFormUrl("");
    setFormAlias("");
    setFormTitle("");
    setFormShortCode("");
    setFormExpiry(null);
    setFormActive(true);
    setSelectedLink(null);
  };

  const copyToClipboard = async (shortCode, linkId) => {
/*    const shortUrl = `${BACKEND_URL}/api/r/${shortCode}`; */
    const shortUrl = `${window.location.origin}/${shortCode}`;
    try {
      const ok = await copyText(shortUrl);
      if (!ok) throw new Error("copy failed");
      setCopiedId(linkId);
      toast.success("Link disalin!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Gagal menyalin link");
    }
  };

  const downloadQR = (link) => {
    if (!link.qr_code) return;
    const linkElement = document.createElement("a");
    linkElement.href = link.qr_code;
    linkElement.download = `qrcode-${link.short_code}.png`;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
    toast.success("QR Code berhasil didownload!");
  };

  const filteredLinks = links.filter(link => 
    link.original_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link.short_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (link.title && link.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="links-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Links
            </h1>
            <p className="text-muted-foreground mt-1">
              Kelola semua short link Anda
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-create-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk Create Links</DialogTitle>
                  <DialogDescription>
                    Masukkan URL (satu per baris, maksimal 50)
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkCreate}>
                  <textarea
                    className="w-full h-40 p-3 rounded-lg border border-input bg-background resize-none"
                    placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                    value={bulkUrls}
                    onChange={(e) => setBulkUrls(e.target.value)}
                    data-testid="bulk-urls-input"
                  />
                  <DialogFooter className="mt-4">
                    <Button type="submit" disabled={isSubmitting} data-testid="bulk-submit-btn">
                      {isSubmitting ? "Creating..." : "Create Links"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-link-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Link
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Link</DialogTitle>
                  <DialogDescription>
                    Perpendek URL Anda dengan opsi kustomisasi. QR Code akan otomatis dibuat.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>URL Asli *</Label>
                    <Input
                      type="url"
                      placeholder="https://example.com/very-long-url"
                      value={formUrl}
                      onChange={(e) => setFormUrl(e.target.value)}
                      data-testid="form-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                  <Label>Custom Alias (opsional)</Label>
                
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      s.stikes-hi.ac.id/
                    </span>
                
                    <div className="relative w-full">
                      <Input placeholder="custom-alias"
                          value={formAlias}
                          onChange={(e) => {
                            const value = e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, "");
                        
                            setFormAlias(value);
                          }}
                          data-testid="form-alias-input"
                        />
                
                      {aliasStatus === "checking" && (
                        <span className="absolute right-2 top-2 text-xs text-muted-foreground">
                          checking...
                        </span>
                      )}
                
                      {aliasStatus === "available" && (
                       <p className="text-xs text-green-500">
                            Alias tersedia
                          </p>
                      )}
                
                      {(aliasStatus === "taken" || aliasStatus === "invalid") && (
                        <span className="absolute right-2 top-2 text-red-500">✕</span>
                      )}
                    </div>
                  </div>
                
                  {aliasStatus === "invalid" && (
                    <p className="text-xs text-red-500">
                      Alias tidak valid atau konflik dengan halaman website
                    </p>
                  )}
                
                  {aliasStatus === "taken" && (
                    <p className="text-xs text-red-500">
                      Alias sudah digunakan
                    </p>
                  )}
                </div>
                  <div className="space-y-2">
                    <Label>Title (opsional)</Label>
                    <Input
                      placeholder="Deskripsi singkat"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      data-testid="form-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date (opsional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="form-expiry-btn"
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {formExpiry ? format(formExpiry, "PPP", { locale: id }) : "Pilih tanggal"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formExpiry}
                          onSelect={setFormExpiry}
                          initialFocus
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Aktif</Label>
                    <Switch
                      checked={formActive}
                      onCheckedChange={setFormActive}
                      data-testid="form-active-switch"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={isSubmitting ||
                    aliasStatus === "taken" ||
                    aliasStatus === "invalid" ||
                    aliasStatus === "checking"
                  }
                  data-testid="form-submit-btn"
                >
                      {isSubmitting ? "Creating..." : "Create Link + QR"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cari link..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>

        {/* Links List */}
        <Card className="dashboard-card">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredLinks.length === 0 ? (
              <div className="text-center py-12">
                <Link2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {searchQuery ? "Tidak ada link yang cocok" : "Belum ada link. Buat link pertama Anda!"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredLinks.map((link) => (
                  <div
                    key={link.link_id}
                    className="p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`link-item-${link.link_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex gap-3 min-w-0 flex-1">
                        {/* QR Code Thumbnail */}
                        {link.qr_code && (
                          <div 
                         /*   className="w-12 h-12 rounded-lg bg-white p-1 cursor-pointer hover:shadow-md transition-shadow flex-shrink-0" */
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-white p-1 cursor-pointer hover:shadow-md transition-shadow flex-shrink-0"
                            onClick={() => openQrDialog(link)}
                          >
                            <img src={link.qr_code} alt="QR Code" className="w-full h-full" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate text-sm sm:text-base" style={{ fontFamily: "Plus Jakarta Sans" }}>
                              {link.title || link.original_url}
                            </p>
                            {!link.is_active && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                                Disabled
                              </span>
                            )}
                            {link.expires_at && new Date(link.expires_at) < new Date() && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-destructive/10 text-destructive">
                                Expired
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-primary font-mono">
                            {window.location.origin}/{link.short_code}
                          </p>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {link.original_url}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
                          {link.clicks} clicks
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0"
                          onClick={() => copyToClipboard(link.short_code, link.link_id)}
                          data-testid={`copy-${link.link_id}`}
                        >
                          {copiedId === link.link_id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        /*  onClick={() => window.open(`${BACKEND_URL}/api/r/${link.short_code}`, "_blank")} */
                          onClick={() => window.open(`${window.location.origin}/${link.short_code}`, "_blank")}
                          data-testid={`open-${link.link_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/analytics/${link.link_id}`)}
                          data-testid={`analytics-${link.link_id}`}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`menu-${link.link_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(link)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openQrDialog(link)}>
                              <QrCode className="w-4 h-4 mr-2" />
                              View QR Code
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadQR(link)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download QR
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(link.link_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Link</DialogTitle>
              <DialogDescription>
                Update detail link Anda. URL asli tidak dapat diubah.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  URL Asli (tidak dapat diubah)
                </Label>
                <Input
                  type="url"
                  value={formUrl}
                  disabled
                  className="bg-muted cursor-not-allowed"
                  data-testid="edit-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Custom Short Code</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    s.stikes-hi.ac.id/
                  </span>
                  <Input
                    value={formShortCode}
                    onChange={(e) => setFormShortCode(e.target.value)}
                    data-testid="edit-shortcode-input"
                    placeholder="custom-alias"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Mengubah short code akan regenerate QR Code
                </p>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Deskripsi singkat"
                  data-testid="edit-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formExpiry ? format(formExpiry, "PPP", { locale: id }) : "Pilih tanggal"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formExpiry}
                      onSelect={setFormExpiry}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex items-center justify-between">
                <Label>Aktif</Label>
                <Switch
                  checked={formActive}
                  onCheckedChange={setFormActive}
                  data-testid="edit-active-switch"
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting} data-testid="edit-submit-btn">
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>QR Code</DialogTitle>
              <DialogDescription>
                {selectedLink?.title || selectedLink?.short_code}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center">
              {selectedLink?.qr_code && (
                <div className="p-4 bg-white rounded-xl mb-4">
                  <img 
                    src={selectedLink.qr_code} 
                    alt="QR Code" 
                    className="w-64 h-64"
                  />
                </div>
              )}
         
              <p className="text-sm text-muted-foreground text-center mb-4">
                  {window.location.origin}/{selectedLink?.short_code}
              </p>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => downloadQR(selectedLink)}
                  data-testid="qr-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={() => copyToClipboard(selectedLink?.short_code, selectedLink?.link_id)}
                  data-testid="qr-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LinksPage;
